import { useCallback, useEffect, useState } from "react";
import { usersService } from "@/services";
import type { StaffUser } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { useConfirm } from "@/context/ConfirmContext";
import { canManageUsers, canViewUsers, canDeleteUser, roleLabel } from "@/config/roles";
import { PageHeader } from "@/components/feature/PageHeader";
import { SettingsNav } from "../components/SettingsNav";
import { Card } from "@/components/base/Card";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { EmptyState } from "@/components/base/EmptyState";
import { ErrorState } from "@/components/base/ErrorState";
import { Avatar } from "@/components/base/Avatar";
import { DataTable, type DataTableColumn } from "@/components/feature/DataTable";
import { formatDateTime } from "@/utils/format";
import { UserFormModal } from "./components/UserFormModal";
import { PasswordResetModal } from "./components/PasswordResetModal";
import { UserDevicesModal } from "./components/UserDevicesModal";

export default function UsersSettingsPage() {
  const { user: current, role } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const canManage = canManageUsers(role);
  const canView = canViewUsers(role);

  const [users, setUsers] = useState<StaffUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<StaffUser | null>(null);
  const [resetTarget, setResetTarget] = useState<StaffUser | null>(null);
  const [devicesTarget, setDevicesTarget] = useState<StaffUser | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setUsers(await usersService.listUsers());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load users.");
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditTarget(null);
    setFormOpen(true);
  };
  const openEdit = (u: StaffUser) => {
    setEditTarget(u);
    setFormOpen(true);
  };

  const handleDelete = async (u: StaffUser) => {
    if (u.id === current?.id) {
      toast.warning("Not allowed", "You cannot delete your own account.");
      return;
    }
    if (usersService.isProtectedUser(u)) {
      toast.warning(
        "Protected demo account",
        "Demonstration accounts cannot be removed."
      );
      return;
    }
    if (
      u.role === "super_admin" &&
      (users ?? []).filter((x) => x.role === "super_admin" && x.status === "active" && x.id !== u.id).length === 0
    ) {
      toast.warning("Not allowed", "Cannot delete the last active Super Admin.");
      return;
    }

    const ok = await confirm({
      title: "Delete User",
      message: `Are you sure you want to permanently delete user "${u.name}" (${u.email})? This action cannot be undone.`,
      confirmLabel: "Delete User",
      destructive: true,
      dangerText: "The user account and its credentials will be permanently removed.",
    });
    if (!ok) return;

    setActingId(u.id);
    try {
      await usersService.deleteUser(u.id);
      toast.success("User deleted", `${u.name} was permanently removed.`);
      await load();
    } catch (err) {
      toast.error("Could not delete user", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setActingId(null);
    }
  };

  const toggleStatus = async (u: StaffUser) => {
    const isLastSuperAdmin =
      u.role === "super_admin" &&
      u.status === "active" &&
      (users ?? []).filter((x) => x.role === "super_admin" && x.status === "active" && x.id !== u.id).length === 0;

    if (u.id === current?.id) {
      toast.warning("Not allowed", "You cannot deactivate your own account.");
      return;
    }
    if (u.status === "active" && usersService.isProtectedUser(u)) {
      toast.warning(
        "Protected demo account",
        "Demonstration accounts cannot be removed while the application is running on mock data. Other users remain fully editable."
      );
      return;
    }
    if (u.status === "active" && isLastSuperAdmin) {
      toast.warning("Not allowed", "Cannot deactivate the last active Super Admin.");
      return;
    }

    const deactivating = u.status === "active";
    const ok = await confirm({
      title: deactivating ? "Deactivate this user?" : "Reactivate this user?",
      message: deactivating
        ? `${u.name} will no longer be able to sign in. Their account and every audit entry and receipt they recorded stay intact.`
        : `${u.name} will regain access to the system.`,
      confirmLabel: deactivating ? "Deactivate" : "Reactivate",
      destructive: deactivating,
      dangerText: deactivating ? "Users are never deleted — deactivation preserves all history." : undefined,
    });
    if (!ok) return;

    setActingId(u.id);
    try {
      await usersService.setUserStatus(u.id, deactivating ? "inactive" : "active", current?.name ?? "Staff", current?.id ?? "");
      toast.success(deactivating ? "User deactivated" : "User reactivated", `${u.name}'s status was updated.`);
      await load();
    } catch (err) {
      toast.error("Could not update status", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setActingId(null);
    }
  };

  const columns: DataTableColumn<StaffUser>[] = [
    {
      id: "name",
      header: "Name",
      accessor: (u) => (
        <div className="flex items-center gap-2.5">
          <Avatar name={u.name} color={u.avatarColor} size="sm" />
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 font-medium text-foreground-900">
              {u.name}
              {usersService.isProtectedUser(u) ? (
                <span className="rounded-full bg-secondary-100 px-1.5 py-0.5 text-[10px] font-medium text-secondary-800">
                  Protected
                </span>
              ) : null}
            </p>
            <p className="text-xs text-foreground-500">{u.email}</p>
          </div>
        </div>
      ),
      sortValue: (u) => u.name,
      sortable: true,
    },
    {
      id: "phone",
      header: "Phone",
      accessor: (u) => <span className="tnum">{u.phone}</span>,
      sortValue: (u) => u.phone,
      sortable: true,
    },
    {
      id: "role",
      header: "Role",
      accessor: (u) => <Badge color={u.role === "super_admin" ? "accent" : "secondary"}>{roleLabel(u.role)}</Badge>,
      sortValue: (u) => roleLabel(u.role),
      sortable: true,
      filterable: true,
      filterOptions: [
        { value: "super_admin", label: "Super Admin" },
        { value: "admin", label: "Admin" },
        { value: "accountant", label: "Accountant" },
        { value: "collection_agent", label: "Collection Agent" },
        { value: "viewer", label: "Viewer" },
      ],
    },
    {
      id: "lastLogin",
      header: "Last login",
      accessor: (u) => <span className="tnum">{u.lastLogin ? formatDateTime(u.lastLogin) : "Never"}</span>,
      sortValue: (u) => u.lastLogin ?? "",
      sortable: true,
    },
    {
      id: "status",
      header: "Status",
      accessor: (u) =>
        u.status === "active" ? (
          <Badge variant="active" dot>Active</Badge>
        ) : (
          <Badge variant="inactive">Inactive</Badge>
        ),
      filterable: true,
      filterOptions: [
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" },
      ],
    },
    {
      id: "actions",
      header: "",
      align: "right",
      width: "220px",
      hideable: false,
      accessor: (u) => (
        <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="ghost" iconOnly icon={<i className="ri-pencil-line" />} title="Edit" disabled={!canManage || u.id === current?.id} onClick={() => openEdit(u)} />
          <Button size="sm" variant="ghost" iconOnly icon={<i className="ri-macbook-line" />} title="Authorized Devices" disabled={!canManage} onClick={() => setDevicesTarget(u)} />
          <Button size="sm" variant="ghost" iconOnly icon={<i className="ri-key-line" />} title="Reset password" disabled={!canManage} onClick={() => setResetTarget(u)} />
          <Button
            size="sm"
            variant="ghost"
            iconOnly
            className={u.status === "active" ? "text-danger-600" : "text-success-600"}
            icon={<i className={u.status === "active" ? "ri-user-unfollow-line" : "ri-user-follow-line"} />}
            title={
              u.status === "active" && usersService.isProtectedUser(u)
                ? "Protected demo account — cannot be deactivated"
                : u.status === "active"
                  ? "Deactivate"
                  : "Reactivate"
            }
            disabled={!canManage || (u.status === "active" && usersService.isProtectedUser(u))}
            loading={actingId === u.id}
            onClick={() => toggleStatus(u)}
          />
          {canDeleteUser(u.role, role, u.id === current?.id) && !usersService.isProtectedUser(u) ? (
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              className="text-danger-600 hover:text-danger-700 hover:bg-danger-50"
              icon={<i className="ri-delete-bin-line" />}
              title="Delete user"
              disabled={actingId === u.id}
              loading={actingId === u.id}
              onClick={() => handleDelete(u)}
            />
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Organization configuration, staff users and the audit trail."
        actions={
          canManage ? (
            <Button icon={<i className="ri-user-add-line" />} onClick={openAdd}>
              Add user
            </Button>
          ) : (
            <span className="text-xs text-foreground-400">Only Super Admin creates users</span>
          )
        }
      />
      <SettingsNav />

      {!canView ? (
        <EmptyState
          icon="ri-lock-line"
          title="Super Admin and Admin only"
          description="Only Super Admin and Admin can access staff users."
        />
      ) : error ? (
        <div className="mb-3">
          <ErrorState message={error} onRetry={load} />
        </div>
      ) : !users ? (
        <div className="h-64 animate-pulse rounded-lg bg-background-200" />
      ) : (
        <Card title="Staff users" subtitle="Users are never deleted — only deactivated, to preserve the audit trail.">
          <DataTable<StaffUser>
            columns={columns}
            rows={users}
            rowKey={(u) => u.id}
            emptyMessage="No users found"
            searchable
            searchPlaceholder="Search by name, email or phone…"
            globalSearch={(u, q) =>
              u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.phone.includes(q)
            }
            defaultPageSize={10}
          />
        </Card>
      )}

      <UserFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        initial={editTarget}
        users={users ?? []}
        currentUserId={current?.id ?? ""}
        actor={current?.name ?? "Staff"}
        onSaved={load}
      />
      <PasswordResetModal open={Boolean(resetTarget)} onClose={() => setResetTarget(null)} user={resetTarget} actor={current?.name ?? "Staff"} />
      <UserDevicesModal open={Boolean(devicesTarget)} onClose={() => setDevicesTarget(null)} user={devicesTarget} />
    </div>
  );
}